/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/**
 * utility functions for parsing MVN uri strings
 */
import DetailedError from '../exceptions/detailedError.js';
import { isCommerceEmbedSharedOptions, isCommerceItemIdentifierSet } from './typeGuards/index.js';
import { isLegacyInfoblockMvnFormat } from './typeGuards/legacy.js';
export function parseCtaMvn(subject) {
    try {
        const mvnUrl = getMvnUrl(subject);
        const config = {};
        const identifiers = extractIdentifierSet(mvnUrl.searchParams);
        const ctaText = mvnUrl.searchParams.get('ctaText');
        const options = extractSharedEmbedOptions(mvnUrl.searchParams);
        const link = mvnUrl.searchParams.get('link');
        if (typeof link !== 'string') {
            throw new Error('Link not specified');
        }
        void new URL(link);
        if (identifiers !== undefined) {
            config.identifiers = identifiers;
        }
        if (typeof ctaText === 'string') {
            config.ctaText = ctaText;
        }
        if (options !== undefined) {
            config.options = options;
        }
        config.link = link;
        return config;
    }
    catch (e) {
        let errorMessage = 'Unknown error';
        if (e instanceof Error) {
            errorMessage = e.message;
        }
        throw new Error('Attempted to parse invalid CTA MVN, ' + errorMessage);
    }
}
export function parseInfoblockMvn(subject) {
    try {
        const url = getMvnUrl(subject);
        const itemDataString = url.searchParams.get('data');
        const optionsString = url.searchParams.get('options');
        if (itemDataString === null) {
            // unable to pull enough data from the definition, bailing
            throw new Error('Unable to retrieve infoblock data from the supplied mvn');
        }
        const parsedItem = JSON.parse(itemDataString);
        if (!isLegacyInfoblockMvnFormat(parsedItem)) {
            throw new Error('MVN does not contain a valid infoblock format');
        }
        const item = buildInfoblockItem(parsedItem);
        const options = optionsString === null ? undefined : JSON.parse(optionsString);
        const infoblockTitle = parsedItem.retrievedData?.title ?? parsedItem.formatted?.title;
        return {
            item,
            title: infoblockTitle,
            options: options,
        };
    }
    catch (e) {
        const msg = e instanceof Error ? e.message : 'unknown';
        throw new Error(`Attempted to parse invalid Infoblock mvn: ${msg}`);
    }
}
function getMvnUrl(subject) {
    if (typeof subject !== 'string' || subject.substring(0, 6) !== 'mvn://') {
        throw new Error('Subject is not a string starting with mvn://');
    }
    try {
        return new URL(subject);
    }
    catch {
        throw new Error('Subject is not parsable as an URL');
    }
}
function extractIdentifierSet(searchParams) {
    const unparsedIdentifiers = searchParams.get('identifiers');
    if (unparsedIdentifiers === null) {
        return undefined;
    }
    try {
        const identifiers = JSON.parse(unparsedIdentifiers);
        if (isCommerceItemIdentifierSet(identifiers)) {
            return identifiers;
        }
    }
    catch {
        throw new DetailedError('Unable to parse identifer string', unparsedIdentifiers);
    }
    return undefined;
}
function extractSharedEmbedOptions(searchParams) {
    const optionsString = searchParams.get('options');
    if (optionsString === null) {
        return undefined;
    }
    try {
        const options = JSON.parse(optionsString);
        if (isCommerceEmbedSharedOptions(options)) {
            return options;
        }
    }
    catch {
        throw new DetailedError('Unable to parse options string', optionsString);
    }
    return undefined;
}
function buildInfoblockItem(parsedItem) {
    if (parsedItem.formatted) {
        return parsedItem.formatted;
    }
    if (parsedItem.retrievedData === undefined) {
        throw new Error('MVN does not contain a valid infoblock definition');
    }
    return {
        title: parsedItem.retrievedData?.title || 'Unknown Title',
        identifiers: { ASIN: parsedItem.retrievedData.identifier },
        offers: [buildEasyAzonDefaultOffer(parsedItem.retrievedData)],
        images: buildEasyAzonImages(parsedItem.retrievedData),
    };
}
function buildEasyAzonDefaultOffer(parsed) {
    if (parsed.offer === undefined) {
        throw new Error('Legacy EazyAzon infoblock format does not have an offer');
    }
    if (parsed.url === undefined) {
        throw new Error('Supplied legacy EazyAzon shortcode does not have a url');
    }
    // The paradeInitialPulldate is a datetime int val representing the
    // pull date of the static data used for parade replatforming.  Once the
    // commerce api is in place, this shim should be removed and a log/notice
    // raised when a product without a pull date is encountered.
    const paradeInitialPulldate = 1656601200000;
    return {
        url: parsed.url,
        vendor: 'Amazon',
        price: parsed.offer.price,
        saved: parsed.offer.saved ?? false,
        pullDate: parsed.offer.pullDate ?? paradeInitialPulldate,
        locale: parsed.locale,
    };
}
function buildEasyAzonImages(parsed) {
    if (parsed?.images === undefined) {
        return [];
    }
    const images = {};
    parsed.images
        .sort((image) => image.width)
        .forEach((image) => {
        const imageKey = extractAmazonProductImageKey(image) ?? 'unknown';
        if (images[imageKey] === undefined) {
            images[imageKey] = { isWebP: false, sizes: [] };
        }
        const identifiedSizes = images[imageKey].sizes?.some((size) => size.src === image.url) ?? false;
        if (!identifiedSizes) {
            images[imageKey]?.sizes?.push({
                src: image.url,
                height: image.height,
                width: image.width,
            });
        }
    });
    return Object.values(images);
}
function extractAmazonProductImageKey(image) {
    if (typeof image !== 'object' || image === null || typeof image.url !== 'string') {
        return undefined;
    }
    const fileChunk = image.url.split('/').pop();
    if (typeof fileChunk !== 'string') {
        return undefined;
    }
    const keyEndPos = fileChunk.indexOf('._SL');
    return keyEndPos === -1 ? undefined : fileChunk.slice(0, keyEndPos);
}
//# sourceMappingURL=mvnParsers.js.map